import { Component, inject, OnInit } from '@angular/core';
import { first } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { EdiRespondeOrder, EdiOrderMessageRelations } from '../../../models/edi';
import { EdiRespondeOrderResponseActionConfigService } from '../../../services/edi-order-response-action-config/edi-order-response-action-config.service';
import { EdiOrderResponseDetailConfigService } from '../../../services/edi-order-response-detail-config/edi-order-response-detail-config.service';
import { EdiService } from '../../../services/edi/edi.service';
import { EdiNavigationService } from '../../../services/edi/edi-navigation.service';
import { EdiRowTable, EdiRowTableType } from '../edi-row-table/edi-row-table.component';
import { DateUtilService } from 'gung-common';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { EDI_VIEWER_FEATURE_ID } from '../../../services/edi/edi-viewer-compatibility.service';
import { TranslateService } from '@ngx-translate/core';

interface SummaryQuantity {
  unit: string;
  value: number;
}

interface SummaryAmount {
  currency: string;
  value: number;
}

interface OrderResponseSummary {
  acceptedLineCount: number;
  changedLineCount: number;
  rejectedLineCount: number;
  unconfirmedLineCount: number;
  orderedQuantities: SummaryQuantity[];
  confirmedQuantities: SummaryQuantity[];
  confirmedAmounts: SummaryAmount[];
  earliestDeliveryDate?: Date;
  latestDeliveryDate?: Date;
}

@Component({
    selector: 'lib-edi-order-response-detail',
    templateUrl: './edi-order-response-detail.component.html',
    styleUrls: ['./edi-order-response-detail.component.css'],
    standalone: false
})
export class EdiOrderResponseDetailComponent implements OnInit {
  // Outgoing messages name their ERP order as the supplier order when erpIds is not filled in
  get erpOrderIds(): string[] {
    if (this.orderResponseData?.erpIds?.length) {
      return this.orderResponseData.erpIds;
    }
    const supplierOrderId = this.orderResponseData?.documentReferences?.supplierOrder?.id;
    return supplierOrderId ? [supplierOrderId] : [];
  }

  relations: EdiOrderMessageRelations | null = null;

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected ediService = inject(EdiService);
  public ediNavigationService = inject(EdiNavigationService);
  public configService = inject(EdiOrderResponseDetailConfigService);
  public actionConfig = inject(EdiRespondeOrderResponseActionConfigService);
  public dateUtilService = inject(DateUtilService);
  protected backendFeatureService = inject(BackendFeatureService);
  protected translateService = inject(TranslateService);
  documentViewerEnabled$ = this.backendFeatureService.isActivated(EDI_VIEWER_FEATURE_ID);
  
  orderResponseId: string;
  orderResponseData: any;
  orderId: string;
  ediNavigationBaseRoute: string;
  responseSummary: OrderResponseSummary = this.emptyResponseSummary();

  get itemCount(): number {
    return this.orderResponseData?.items?.length || 0;
  }

  get changedLineCount(): number {
    return (this.orderResponseData?.items || []).filter(item => {
      const expected = item?.quantities?.expectedQuantity;
      const actual = item?.quantities?.actualQuantity;
      return expected !== undefined && expected !== null && actual !== undefined && actual !== null && Number(expected) !== Number(actual);
    }).length;
  }

  get hasDeliveryWindow(): boolean {
    return !!this.responseSummary.earliestDeliveryDate;
  }

  get hasDeliveryDateRange(): boolean {
    const earliest = this.responseSummary.earliestDeliveryDate?.getTime();
    const latest = this.responseSummary.latestDeliveryDate?.getTime();
    return earliest !== undefined && latest !== undefined && earliest !== latest;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(value);
  }

  formatAmount(amount: SummaryAmount): string {
    const value = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount.value);
    return `${value}${amount.currency ? ` ${amount.currency}` : ''}`;
  }

  formatItemIds(item: EdiRespondeOrder): string {
    const ids = item?.ids;
    return [
      ids?.gungId ? `${this.translateService.instant('ID')}: ${ids.gungId}` : '',
      ids?.ean13 ? `${this.translateService.instant('EAN')}: ${ids.ean13}` : '',
      ids?.upca ? `${this.translateService.instant('UPCA')}: ${ids.upca}` : '',
      ids?.buyerId ? `${this.translateService.instant('BUYER_ID')}: ${ids.buyerId}` : ''
    ].filter(Boolean).join('\n');
  }

  itemsColumns: EdiRowTable[] = [
    {
      header: 'IMAGE',
      type: EdiRowTableType.IMAGE,
      getValue: (item: EdiRespondeOrder) => item?.extra?.images
    },
    {
      header: 'ITEM_IDS',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiRespondeOrder) => this.formatItemIds(item),
      customClass: 'formatted'
    }, {
      header: 'EXPECTED_QUANTITY',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiRespondeOrder) => item.quantities?.expectedQuantity
    },
    {
      header: 'ACTUAL_QUANTITY',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiRespondeOrder) => item.quantities?.actualQuantity
    },
    {
      header: 'UNIT_NET_PRICE',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiRespondeOrder) =>
        (item.prices?.actualUnitNetPrice?.value || '') + ' ' + (item.prices?.actualUnitNetPrice?.currency || '')
    },
    {
      header: 'UNIT_GROSS_PRICE',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiRespondeOrder) =>
        (item.prices?.actualUnitGrossPrice?.value || '') + ' ' + (item.prices?.actualUnitGrossPrice?.currency || '')
    },
    {
      header: 'DELIVERY_DATE',
      type: EdiRowTableType.DATE,
      getValue: (item: EdiRespondeOrder) => item.deliveryDate
    },
    {
      header: 'TOTAL_NET_PRICE',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiRespondeOrder) =>
        (item.prices?.actualTotalNetPrice?.value || '') + ' ' + (item.prices?.actualTotalNetPrice?.currency || '')
    }
  ];

  ngOnInit(): void {
    this.route.params.subscribe(data => {
      this.orderResponseId = data.id;

      const urlSegments = this.router.url.split('?')[0].split('/');
      this.ediNavigationBaseRoute = urlSegments.slice(0, -1).join('/');
      this.ediNavigationService.setNavigationCurrentId(this.ediNavigationBaseRoute, this.orderResponseId);

      this.ediService.getOrderResponse(this.orderResponseId).subscribe(orderResponseData => {
        this.orderResponseData = orderResponseData.data;
        this.ediService
          .getDocumentRelations(this.orderResponseData.orderMessageId)
          .pipe(first())
          .subscribe(relations => (this.relations = relations));
        this.responseSummary = this.buildResponseSummary(this.orderResponseData);
      });
    });
  }

  private emptyResponseSummary(): OrderResponseSummary {
    return {
      acceptedLineCount: 0,
      changedLineCount: 0,
      rejectedLineCount: 0,
      unconfirmedLineCount: 0,
      orderedQuantities: [],
      confirmedQuantities: [],
      confirmedAmounts: []
    };
  }

  private buildResponseSummary(message: any): OrderResponseSummary {
    const summary = this.emptyResponseSummary();
    const items = message?.items || [];
    const orderedQuantities = new Map<string, number>();
    const confirmedQuantities = new Map<string, number>();
    const confirmedAmounts = new Map<string, number>();
    const deliveryDates: Date[] = [];

    for (const item of items) {
      const expected = this.toNumber(item?.quantities?.expectedQuantity);
      const actual = this.toNumber(item?.quantities?.actualQuantity);
      const cancelled = item?.changes?.cancelled === true;
      const rejected = cancelled || (expected !== null && expected > 0 && actual === 0);

      if (rejected) {
        summary.rejectedLineCount++;
      } else if (actual === null) {
        summary.unconfirmedLineCount++;
      } else if (expected !== null && actual === expected) {
        summary.acceptedLineCount++;
      } else {
        summary.changedLineCount++;
      }

      const unit = String(item?.unitOfMeasure || '').trim();
      this.addToTotal(orderedQuantities, unit, expected);
      this.addToTotal(confirmedQuantities, unit, actual);

      if (!rejected && actual !== null) {
        const totalNetPrice = this.toNumber(item?.prices?.actualTotalNetPrice?.value);
        const unitNetPrice = this.toNumber(item?.prices?.actualUnitNetPrice?.value);
        const amount = totalNetPrice ?? (unitNetPrice !== null ? unitNetPrice * actual : null);
        const currency = String(
          item?.prices?.actualTotalNetPrice?.currency || item?.prices?.actualUnitNetPrice?.currency || message?.currency || ''
        ).trim();
        this.addToTotal(confirmedAmounts, currency, amount);
      }

      const deliveryDate = this.toDate(item?.deliveryDate);
      if (deliveryDate) deliveryDates.push(deliveryDate);
    }

    const headerDeliveryDate = this.toDate(message?.deliveryDate);
    if (headerDeliveryDate) deliveryDates.push(headerDeliveryDate);
    deliveryDates.sort((a, b) => a.getTime() - b.getTime());

    summary.orderedQuantities = this.mapTotals(orderedQuantities);
    summary.confirmedQuantities = this.mapTotals(confirmedQuantities);
    summary.confirmedAmounts = Array.from(confirmedAmounts, ([currency, value]) => ({ currency, value }));
    summary.earliestDeliveryDate = deliveryDates[0];
    summary.latestDeliveryDate = deliveryDates[deliveryDates.length - 1];
    return summary;
  }

  private addToTotal(totals: Map<string, number>, key: string, value: number | null): void {
    if (value === null) return;
    totals.set(key, (totals.get(key) || 0) + value);
  }

  private mapTotals(totals: Map<string, number>): SummaryQuantity[] {
    return Array.from(totals, ([unit, value]) => ({ unit, value }));
  }

  private toNumber(value: unknown): number | null {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  private toDate(value: unknown): Date | null {
    if (!value) return null;
    const date = new Date(value as string | number | Date);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
